import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, message, Input, Row, Col, Button, Space } from "antd";
import moment from "moment";

function UsersList() {
  const [users, setUsers] = useState([]);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Fetch users data
  const getUserData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const response = await axios.get("/api/admin/get-all-users", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      dispatch(hideLoading());
      message.error("Error fetching user data");
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  // Block user API call handler
  const handleBlock = async (userId) => {
    try {
      dispatch(showLoading());
      const response = await axios.put(
        `/api/admin/block-user/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        message.success("User blocked successfully");
        setUsers((prev) =>
          prev.map((user) =>
            user._id === userId ? { ...user, isBlocked: true } : user
          )
        );
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      dispatch(hideLoading());
      message.error("Error blocking user");
      console.error(error);
    }
  };

  // Handle table change for sorting and filtering
  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Clear filters only
  const clearFilters = () => {
    setFilteredInfo({});
  };

  // Clear filters and sorters
  const clearAll = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchTerm("");
  };

  // Search input handler (kept for quick text search filtering on email)
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Columns with sorting and filtering integrated with state
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      sortOrder: sortedInfo.columnKey === "email" ? sortedInfo.order : null,
      ellipsis: true,
      // Optional: add filter dropdown here if you want
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
      sortOrder: sortedInfo.columnKey === "phone" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      filters: [
        { text: "Admin", value: "admin" },
        { text: "Doctor", value: "doctor" },
            { text: "Patient", value: "patient" },
        // Add other roles here if needed
      ],
      filteredValue: filteredInfo.role || null,
      onFilter: (value, record) => record.role === value,
      sorter: (a, b) => a.role.localeCompare(b.role),
      sortOrder: sortedInfo.columnKey === "role" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      sortOrder: sortedInfo.columnKey === "createdAt" ? sortedInfo.order : null,
      render: (text, record) => (
        <span key={record._id + "-created"}>
          {moment(record.createdAt).format("YYYY-MM-DD | HH:mm:ss")}
        </span>
      ),
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div key={record._id + "-actions"} className="d-flex">
          {record.isBlocked ? (
            <span
              className="anchor"
              style={{ color: "red", cursor: "default" }}
            >
              Blocked
            </span>
          ) : (
            <span
              className="anchor"
              style={{ color: "blue", cursor: "pointer" }}
              onClick={() => handleBlock(record._id)}
            >
              Block
            </span>
          )}
        </div>
      ),
    },
  ];

  // Filter users by searchTerm in email before passing to Table
  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div>
      
        <Row gutter={20} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={24} lg={6}>
            <Input
              placeholder="Search by Email..."
              value={searchTerm}
              onChange={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} lg={18}>
            <Space>
              <Button onClick={clearFilters}>Clear Filters</Button>
              <Button onClick={clearAll}>Clear Filters & Sorters</Button>
            </Space>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={filteredUsers.map((user) => ({
            ...user,
            key: user._id || user.email,
          }))}
          pagination={{ pageSize: 10 }}
          onChange={handleChange}
          rowKey="_id"
          bordered
        />
      </div>
    </Layout>
  );
}

export default UsersList;
